import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import styles from './Onglets.module.css';

export interface Onglet {
  id: string;
  libelle: ReactNode;
  contenu: ReactNode;
}

export interface ProprietesOnglets {
  onglets: Onglet[];
  libelle: string;
  /** Onglet actif contrôlé (sinon géré localement). */
  actif?: string;
  onChanger?: (id: string) => void;
}

/** Onglets ARIA : flèches gauche et droite, Début et Fin ; activation à la sélection. */
export function Onglets({ onglets, libelle, actif, onChanger }: ProprietesOnglets) {
  const base = useId();
  const [local, setLocal] = useState(onglets[0]?.id ?? '');
  const courant = actif ?? local;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const choisir = (id: string) => {
    setLocal(id);
    onChanger?.(id);
    refs.current[id]?.focus();
  };

  const clavier = (e: KeyboardEvent<HTMLDivElement>) => {
    const index = onglets.findIndex((o) => o.id === courant);
    let cible = -1;
    if (e.key === 'ArrowRight') cible = (index + 1) % onglets.length;
    else if (e.key === 'ArrowLeft') cible = (index - 1 + onglets.length) % onglets.length;
    else if (e.key === 'Home') cible = 0;
    else if (e.key === 'End') cible = onglets.length - 1;
    if (cible >= 0) {
      e.preventDefault();
      const o = onglets[cible];
      if (o) choisir(o.id);
    }
  };

  return (
    <div>
      <div role="tablist" aria-label={libelle} className={styles.liste} onKeyDown={clavier}>
        {onglets.map((o) => (
          <button
            key={o.id}
            ref={(el) => {
              refs.current[o.id] = el;
            }}
            type="button"
            role="tab"
            id={`${base}-onglet-${o.id}`}
            aria-selected={o.id === courant}
            aria-controls={`${base}-panneau-${o.id}`}
            tabIndex={o.id === courant ? 0 : -1}
            className={styles.onglet}
            onClick={() => choisir(o.id)}
          >
            {o.libelle}
          </button>
        ))}
      </div>
      {onglets.map((o) =>
        o.id === courant ? (
          <div
            key={o.id}
            role="tabpanel"
            id={`${base}-panneau-${o.id}`}
            aria-labelledby={`${base}-onglet-${o.id}`}
            tabIndex={0}
            className={styles.panneau}
          >
            {o.contenu}
          </div>
        ) : null,
      )}
    </div>
  );
}
